import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"
import { useKeycloak } from "@react-keycloak/web"
import type { Employee, WorkDay, AppData, WorkStatus } from "./types"
import { migrateData } from "./data-migration"
import { RemoteTrackerApi, ApiError } from "./api-client"
import { toast } from "@/hooks/use-toast"
import { useAuth } from "./auth-context"
import { getMonthDays, formatDate } from "./date-utils"

/**
 * Data context interface
 * Manages employee data and work day records
 */
interface DataContextType {
  employees: Employee[]
  workDays: WorkDay[]
  loading: boolean
  setWorkStatus: (userId: string, date: string, status: WorkStatus) => Promise<void>
  getWorkStatus: (userId: string, date: string) => WorkStatus
  getEmployeeStats: (userId: string, year: number, month: number) => {
    remote: number
    dayOff: number
    office: number
  }
  refreshWorkDays: (year: number, month: number, filters?: { status?: WorkStatus; userId?: string }) => Promise<void>
  refreshEmployees: () => Promise<void>
}

const DataContext = createContext<DataContextType | undefined>(undefined)

const STORAGE_KEY = "remote-tracker-data"

/**
 * Initial empty data - will be populated from API
 */
const initialData: AppData = {
  employees: [],
  workDays: [],
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { keycloak, initialized } = useKeycloak()
  const { selectedRole } = useAuth()
  const [data, setData] = useState<AppData>(initialData)
  const [loading, setLoading] = useState(false)

  // Load work days from localStorage (fallback) and migrate if necessary
  // Note: We don't store employees in localStorage - they come from API
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        const migrated = migrateData(parsed)
        // Only restore workDays, not employees
        setData(prev => ({
          ...prev,
          workDays: migrated.workDays
        }))
      } catch (e) {
        console.error("Failed to parse stored data:", e)
      }
    }
  }, [])

  // Save workDays to localStorage (not employees - they come from API)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ 
      employees: [],  // Don't cache employees
      workDays: data.workDays 
    }))
  }, [data.workDays])

  /**
   * Refresh work days from API for a specific month
   * @param filters - Optional filters for admin (status, userId)
   */
  const refreshWorkDays = useCallback(async (
    year: number, 
    month: number,
    filters?: { status?: WorkStatus; userId?: string }
  ) => {
    if (!initialized || !keycloak.authenticated || !keycloak.token) {
      console.warn("[DataContext] Cannot fetch work days - not authenticated")
      return
    }

    setLoading(true)
    try {
      // Check if user is admin using auth-context (same logic as UI)
      const isAdmin = selectedRole === "admin"
      
      // For admin users, fetch all work days
      if (isAdmin) {
        
        const response = await RemoteTrackerApi.getAllWorkDays(
          keycloak.token,
          year,
          month + 1, // API uses 1-12, JS Date uses 0-11
          filters // Pass filters to API
        )

        // Transform API response to internal format
        // Admin endpoint doesn't return id, so we generate composite keys
        const workDays: WorkDay[] = response.workDays.map((wd) => ({
          id: `${wd.userId}-${wd.date}`, // Composite key: userId-date
          userId: wd.userId,
          date: wd.date,
          status: wd.status,
          updatedAt: wd.updatedAt,
        }))

        setData((prev) => ({
          ...prev,
          workDays,
        }))
      } else {
        // For employees, fetch all work days (to see colleagues' schedules)
        const userId = keycloak.tokenParsed?.sub
        if (!userId) {
          console.error("[DataContext] No user ID in token")
          return
        }

        const response = await RemoteTrackerApi.getAllWorkDays(
          keycloak.token,
          year,
          month + 1, // API uses 1-12, JS Date uses 0-11
          undefined // No filters - get all employees' work days
        )

        // Transform API response to internal format
        const workDays: WorkDay[] = response.workDays.map((wd) => ({
          id: `${wd.userId}-${wd.date}`, // Composite key: userId-date
          userId: wd.userId,
          date: wd.date,
          status: wd.status,
          updatedAt: wd.updatedAt,
        }))

        
        setData((prev) => ({
          ...prev,
          workDays,
        }))
      }
    } catch (error) {
      console.error("[DataContext] Failed to fetch work days:", error)
      if (error instanceof ApiError) {
        toast({
          title: "Failed to load work days",
          description: error.message,
          variant: "destructive",
        })
      }
    } finally {
      setLoading(false)
    }
  }, [initialized, keycloak, selectedRole])

  /**
   * Refresh employees list from API
   * Available to all authenticated users (employees need to see colleagues)
   */
  const refreshEmployees = useCallback(async () => {

    
    if (!initialized || !keycloak.authenticated || !keycloak.token) {
      console.warn("[DataContext] Cannot fetch employees - not authenticated")
      return
    }


    setLoading(true)
    try {
      const response = await RemoteTrackerApi.getAllEmployees(keycloak.token)
      
      
      
      setData((prev) => ({
        ...prev,
        employees: response.employees,
      }))
      
      toast({
        title: "Employees loaded",
        description: `Loaded ${response.employees.length} employees from backend`,
      })
    } catch (error) {
      console.error("[DataContext] ❌ Failed to fetch employees from backend:", error)
      
      if (error instanceof ApiError) {
        console.error(`[DataContext] API Error ${error.statusCode}:`, error.message)
        toast({
          title: "Failed to load employees",
          description: `${error.statusCode}: ${error.message}`,
          variant: "destructive",
        })
      }
      
      console.warn("[DataContext] ⚠️ Failed to load employees - dashboard will show empty")
      
      // Set empty array if fetch fails
      setData((prev) => ({
        ...prev,
        employees: []
      }))
    } finally {
      setLoading(false)
    }
  }, [initialized, keycloak, selectedRole])

  /**
   * Sets the work status for a specific employee and date
   * Syncs with backend API
   * 
   * @param userId - The employee's ID
   * @param date - The date in YYYY-MM-DD format
   * @param status - The work status (office, remote, or day_off)
   */
  const setWorkStatus = useCallback(async (userId: string, date: string, status: WorkStatus) => {
    // Optimistically update UI
    const previousWorkDays = data.workDays
    
    setData((prev) => {
      if (status === 'office') {
        // Remove work day record (office is implicit/default)
        return {
          ...prev,
          workDays: prev.workDays.filter(
            (wd) => !(wd.userId === userId && wd.date === date)
          ),
        }
      } else {
        // Add or update work day record
        const existingIndex = prev.workDays.findIndex(
          (wd) => wd.userId === userId && wd.date === date
        )

        if (existingIndex !== -1) {
          // Update existing record
          const updated = [...prev.workDays]
          updated[existingIndex] = { ...updated[existingIndex], status }
          return {
            ...prev,
            workDays: updated,
          }
        } else {
          // Create new record
          return {
            ...prev,
            workDays: [
              ...prev.workDays,
              { id: crypto.randomUUID(), userId, date, status },
            ],
          }
        }
      }
    })

    // Sync with backend
    if (initialized && keycloak.authenticated && keycloak.token) {
      try {
        if (status === 'office') {
          // Delete the work day entry
          await RemoteTrackerApi.deleteWorkDay(keycloak.token, userId, date)
        } else {
          // Create or update work day entry
          await RemoteTrackerApi.createOrUpdateWorkDay(keycloak.token, userId, {
            date,
            status,
          })
        }
      } catch (error) {
        console.error("[DataContext] Failed to sync work status:", error)
        
        // Rollback optimistic update
        setData((prev) => ({
          ...prev,
          workDays: previousWorkDays,
        }))

        // Show error message
        if (error instanceof ApiError) {
          toast({
            title: "Failed to update status",
            description: error.message,
            variant: "destructive",
          })
        } else {
          toast({
            title: "Failed to update status",
            description: "Please try again later",
            variant: "destructive",
          })
        }

        throw error
      }
    }
  }, [data.workDays, initialized, keycloak])

  /**
   * Gets the work status for a specific employee and date
   * 
   * @param userId - The employee's ID
   * @param date - The date in YYYY-MM-DD format
   * @returns The work status (defaults to 'office' if no record exists)
   */
  const getWorkStatus = useCallback((userId: string, date: string): WorkStatus => {
    const workDay = data.workDays.find(
      (wd) => wd.userId === userId && wd.date === date
    )
    return workDay?.status ?? 'office'
  }, [data.workDays])

  /**
   * Gets statistics for an employee for a specific month
   * 
   * @param userId - The employee's ID
   * @param year - The year
   * @param month - The month (0-11, JavaScript Date format)
   * @returns Object with counts for each status
   */
  const getEmployeeStats = useCallback((userId: string, year: number, month: number) => {
    // Get all days in the month
    const monthDays = getMonthDays(year, month)

    let remote = 0
    let dayOff = 0
    let office = 0

    monthDays.forEach((date) => {
      const dateStr = formatDate(date)
      const status = getWorkStatus(userId, dateStr)
      
      if (status === 'remote') remote++
      else if (status === 'day_off') dayOff++
      else office++
    })

    return { remote, dayOff, office }
  }, [data.workDays, getWorkStatus])

  return (
    <DataContext.Provider
      value={{
        employees: data.employees,
        workDays: data.workDays,
        loading,
        setWorkStatus,
        getWorkStatus,
        getEmployeeStats,
        refreshWorkDays,
        refreshEmployees,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

export function useData() {
  const context = useContext(DataContext)
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider")
  }
  return context
}
